import { IStyleFactory } from "esdm-map/dist/layers/vector/styles/IStyleFactory";
import Feature from "ol/Feature";
import Style, { StyleFunction } from "ol/style/Style";
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';
import Stroke from 'ol/style/Stroke';
import RegularShape from 'ol/style/RegularShape';
import Text from 'ol/style/Text';
import * as olHas from 'ol/has';
import { LhlpRecordSubType, LhlpRecordType } from "../defs/LhlpRecordType";
import { LhlpMapConfig, LhlpStatusLookup, LhlpTypeLookup } from "../defs/LhlpMapConfig";

export class LhlpStyleFactory implements IStyleFactory {

    private mapConfig: LhlpMapConfig;

    constructor(mapconfig: LhlpMapConfig) {
        this.mapConfig = mapconfig;
    }

    public getStyle(recordType: LhlpRecordType, recordSubType: LhlpRecordSubType = null): (Style | Style[] | StyleFunction) {

        let style: (Style | Style[] | StyleFunction) = null;

        if(recordType === LhlpRecordType.ASSET) {
            return this.setAssetDynamicStyle(recordSubType); //returns a style function
        }

        //try and resolve sub type
        switch (recordSubType) {
            case LhlpRecordSubType.Battlefield:
                style = this.ConservationArea();
                break;

            case LhlpRecordSubType.ListedBuilding:
                style = this.ListedBuilding();
                break;

            case LhlpRecordSubType.ScheduledMonument:
                style = this.ScheduledMonument();
                break;

            case LhlpRecordSubType.RegisteredParkAndGarden:
                style = this.RegisteredParksAndGardens();
                break;

            case LhlpRecordSubType.ProtectedWreck:
                style = this.AreasArchaeologicalImportance();
                break;

            case LhlpRecordSubType.WorldHeritageSite:
                style = this.AreasArchaeologicalImportance();
                break;

        }

        return style;
    }

    public getSelectedStyle(
        recordType: LhlpRecordType = LhlpRecordType.NHLE,
        recordSubType: LhlpRecordSubType = null
        ): (Style | Style[] | StyleFunction)
    {        
        return this.SelectedRecord();
    }

    private setAssetDynamicStyle(featureSubType: LhlpRecordSubType): StyleFunction {
        
        let stylesConfig: LhlpMapConfig = this.mapConfig;

        return function(feature: Feature) {

            let featureStatus: string   = feature.get('asset_status_id');
            if(typeof(featureStatus) == 'undefined') featureStatus = "0";  //get the default colour of listed features       
            let iconChr: string         = '\uf60e';
            let iconColour: string      = '#F00000';
            let siteListedColour: string = '#000000';

            let featureGeometryType: string = feature.getGeometry().getType();

            let statusStyles: Array<LhlpStatusLookup> = stylesConfig.StatusStyles;
            let matchedStatus: boolean = false;
            if(statusStyles !== null) {
                for(let statusStyle of statusStyles) {
                    if(statusStyle.StatusId == '0') {
                        siteListedColour = statusStyle.StatusColour;
                    }
                    if(statusStyle.StatusId === featureStatus) {
                        iconColour = statusStyle.StatusColour;
                        matchedStatus = true;
                        break;
                    }
                }
                if(!matchedStatus) iconColour = siteListedColour;
            }

            let typeStyles: Array<LhlpTypeLookup> = stylesConfig.TypeStyles;
            if(typeStyles !== null) {
                for(let typeStyle of typeStyles) {
                    if(typeStyle.TypeId == featureSubType) {
                        let c: string = typeStyle.TypeCharacter;
                        iconChr = String.fromCharCode(parseInt(c));
                        break;
                    }
                }
            }

            let text = new Text({
                text: iconChr,
                font: '900 23px "Font Awesome 6 Pro"',
                textBaseline: 'bottom',
                offsetY: 11,
                fill: new Fill({
                    color: iconColour,
                }),
                stroke: new Stroke({
                    color: 'rgba(255,255,255,0.8)',
                    width: 5,
                }),
            });

            if(featureGeometryType === 'Point' || featureGeometryType === 'MultiPoint') {
                return new Style({ text: text }) ;
            }
            //polygons
            return [
                new Style({
                    stroke: new Stroke({
                        color: iconColour,
                        width: 3,
                    }),
                    fill: new Fill({
                        color: iconColour + '20',
                    }),
                }),
            ];
        }
    }
    
    private CommonStyle(fillColour: string): Style[] {
        var fill = new Fill({
            color: fillColour
        });
        var stroke = new Stroke({
            color: 'rgba(255,255,255,1)',
            width: 3
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: stroke,
                    radius: 12
                }),
                fill: fill,
                stroke: stroke
            })
        ];
    }

    private Asset(): Style[] {
        //default
        var fill = new Fill({
            color: 'rgba(16, 16, 16, 0.6)'
        });
        var pointfill = new Fill({
            color: 'rgba(0, 0, 0, 0.9)'
        });
        var stroke = new Stroke({
            color: '#000000',
            width: 2
        });

        return [
            new Style({
                image: new RegularShape({
                    fill: pointfill,
                    stroke: stroke,
                    points: 4,
                    radius: 8,
                    angle: Math.PI / 4,
                }),
                fill: fill,
                stroke: stroke
            })
        ];
    }

    private Event(): Style[] {
        var fill = new Fill({
            color: 'rgba(0, 0, 205, 0.5)'
        });
        var pointfill = new Fill({
            color: 'rgba(0, 0, 128, 0.9)'
        });
        var stroke = new Stroke({
            color: 'rgba(0, 0, 128, 1)',
            width: 2
        });

        return [
            new Style({
                image: new RegularShape({
                    fill: pointfill,
                    stroke: stroke,
                    points: 4,
                    radius: 4,
                    angle: Math.PI / 4,
                }),
                fill: fill,
                stroke: stroke
            })
        ];
    }

    private AreasArchaeologicalImportance(): Style[] {
        //return this.CommonStyle('rgba(239, 26, 28,0.5)');            
        // Generate a canvasPattern
        var canvas = document.createElement('canvas');
        var context = canvas.getContext('2d');
        var p = document.createElement('canvas');
        // Gradient and pattern are in canvas pixel space, so we adjust for the
        // renderer's pixel ratio
        var pixelRatio = olHas.DEVICE_PIXEL_RATIO;
        var pattern = (function () {
            canvas.width = 32 * pixelRatio;
            canvas.height = 16 * pixelRatio;
            // background
            context.fillStyle = "rgba(239, 26, 28, 0.1)";
            context.fillRect(0, 0, canvas.width, canvas.height);

            var x0 = 36;
            var x1 = -4;
            var y0 = -2;
            var y1 = 18;
            var offset = 32;

            context.strokeStyle = "rgba(255, 0, 0, 1)";
            context.lineWidth = 1;
            context.beginPath();
            context.moveTo(x0, y0);
            context.lineTo(x1, y1);
            context.moveTo(x0 - offset, y0);
            context.lineTo(x1 - offset, y1);
            context.moveTo(x0 + offset, y0);
            context.lineTo(x1 + offset, y1);
            context.stroke();

            context.fillStyle = context.createPattern(canvas, 'repeat');
            context.fillRect(0, 0, canvas.width, canvas.height);

            return context.createPattern(canvas, 'repeat');
        }());

        var fill = new Fill({
            color: 'rgba(239, 26, 28, 0.4)'
        });
        fill.setColor(pattern);

        var stroke = new Stroke({
            color: 'rgba(239, 26, 28, 1)',
            width: 2
        });

        return [new Style({
            image: new RegularShape({
                fill: new Fill({ color: 'rgba(239, 26, 28, 0.4)' }),
                stroke: stroke,
                points: 3,
                radius: 8,
                radius2: 4,
                angle: 0
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private ScheduledMonument(): Style[] {
        return [
            new Style({
                fill: new Fill({
                    color: 'rgba(255, 0, 255, 0.6)'
                }),
                stroke: new Stroke({
                    color: 'rgba(186, 85, 211, 1)',
                    width: 2
                })
            })
        ];
    }

    private ListedBuilding(): Style[] {
        var fill = new Fill({
            color: 'rgba(255, 234, 0, 0.6)'
        });
        var stroke = new Stroke({
            color: 'rgba(0, 0, 0, 1.0)',
            width: 2
        });
        var pointfill = new Fill({
            color: 'rgba(255, 234, 0, 0.6)'
        });
        return [new Style({
            image: new Circle({
                fill: pointfill,
                stroke: stroke,
                radius: 4
            }),
            fill: fill,
            stroke: stroke
        })];
    }


    private RegisteredParksAndGardens(): Style[] {
        return [
            new Style({
                fill: new Fill({
                    color: 'rgba(0, 255, 0, 0.6)'
                }),
                stroke: new Stroke({
                    color: 'rgba(50, 205, 50, 1)',
                    width: 2
                })
            })
        ];
    }


    private ConservationArea(): Style[] {
        var fill = new Fill({
            color: 'rgba(135, 206, 235, 0.5)'
        });
        var pointfill = new Fill({
            color: 'rgba(135, 206, 235, 0.9)'
        });
        var stroke = new Stroke({
            color: 'rgba(0, 191, 255, 1)',
            width: 2
        });

        return [
            new Style({
                image: new Circle({
                    fill: pointfill,
                    stroke: stroke,
                    radius: 4
                }),
                fill: fill,
                stroke: stroke
            })
        ];
    }



    private SelectedRecord(): Style[] {
        var fill = new Fill({
            color: 'rgba(255, 255, 0, 0.5)'
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: new Stroke({
                        color: 'rgba(255, 173, 10, 1)',
                        width: 3
                    }),
                    radius: 10
                }),
                fill: fill,
                stroke: new Stroke({
                    color: 'rgba(255, 173, 10, 1)',
                    width: 3
                })
            })
        ];
    }

}
